import React, { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import s from './LandingPage.module.css'

const features = [
  { icon: '📊', color: '#deecf9', title: 'Gantt Chart Scheduling', desc: 'Visual resource allocation with day, week, month, quarter, and year views. Color-coded utilization at a glance.' },
  { icon: '💰', color: '#e6f4ea', title: 'Revenue Forecasting', desc: '6-month rolling revenue projections by project. Multi-currency support with live exchange rates.' },
  { icon: '⚡', color: '#fff4ce', title: 'Forecast vs Actual', desc: 'Compare planned allocations against real timesheet data. Identify variances before they become problems.' },
  { icon: '📈', color: '#fde7e9', title: 'Profitability Analysis', desc: 'Per-project P&L with margin tracking. Know which clients and projects drive the most value.' },
  { icon: '👥', color: '#f0f8ff', title: 'Resource Utilization', desc: 'Track capacity, over-booking, and bench time across every resource in your organization.' },
  { icon: '🔒', color: '#faf3e0', title: 'Role-Based Access', desc: 'Admins, Managers, and Viewers with fine-grained permission gates. Full audit trail on every change.' },
]

const plans = [
  {
    name: 'Starter', amount: '79', period: '/month',
    desc: 'Perfect for small teams getting started with resource planning.',
    features: [
      '3 manager/admin seats included',
      'Unlimited viewers (read-only)',
      'Unlimited resource users (timesheet-only)',
      'Up to 15 active projects',
      'Basic reports',
      'Email support'
    ],
    popular: false, btnClass: 'planBtnDefault',
  },
  {
    name: 'Professional', amount: '199', period: '/month',
    desc: 'For growing teams that need full forecasting and analytics.',
    features: [
      '15 manager/admin seats included',
      'Unlimited viewers (read-only)',
      'Unlimited resource users (timesheet-only)',
      '$15/month per additional seat',
      'Unlimited projects',
      'Partner rate sheets',
      'Revenue forecasting',
      'Gantt charts',
      'Advanced reporting',
      'Priority support'
    ],
    popular: true, btnClass: 'planBtnPrimary',
  },
  {
    name: 'Enterprise', amount: '499', period: '/month',
    desc: 'For large organizations needing advanced controls and SLAs.',
    features: [
      '50 manager/admin seats included',
      'Unlimited viewers (read-only)',
      'Unlimited resource users (timesheet-only)',
      '$12/month per additional seat',
      'Unlimited projects',
      'All Professional features',
      'SSO (Azure AD, Google, Okta)',
      'Advanced audit logs',
      'API access',
      'Custom integrations',
      'Dedicated support',
      'SLA guarantees'
    ],
    popular: false, btnClass: 'planBtnDefault',
  },
]

const testimonials = [
  { quote: 'AllocatIQ gave us full visibility into our delivery team for the first time. The Gantt view replaced three different spreadsheets overnight.', name: 'Sarah M.', role: 'VP of Delivery', color: '#0078d4' },
  { quote: 'The revenue forecast report alone paid for the subscription in the first month. We caught a gap three months out and staffed up in time.', name: 'James K.', role: 'COO, Professional Services', color: '#107c10' },
  { quote: "We've tried five tools. Nothing else gives us this combination of Gantt scheduling and financial analytics in one clean interface.", name: 'Priya S.', role: 'Director of PMO', color: '#ca5010' },
]

export default function LandingPage() {
  const navigate = useNavigate()
  const { user, organization, loading } = useAuth()

  useEffect(() => {
    console.log('[Landing] Effect triggered - loading:', loading, 'user:', !!user, 'organization:', !!organization)

    if (!loading) {
      if (user && organization) {
        console.log('[Landing] User has organization, redirecting to /app')
        navigate('/app', { replace: true })
      } else if (user && !organization) {
        console.log('[Landing] User without organization, redirecting to /onboarding')
        navigate('/onboarding', { replace: true })
      } else {
        console.log('[Landing] No user, staying on landing page')
      }
    }
  }, [user, organization, loading, navigate])

  if (loading) {
    return null
  }

  return (
    <div className={s.page}>
      <nav className={s.navbar}>
        <div className={s.navInner}>
          <Link to="/" className={s.navBrand}>
            <div className={s.navLogo}>
              <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" aria-hidden="true">
                <path d="M10 26L18 10L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 21H23" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className={s.navName}>AllocatIQ</span>
          </Link>
          <ul className={s.navLinks}>
            <li><a href="#features">Features</a></li>
            <li><a href="#pricing">Pricing</a></li>
            <li><Link to="/faq">FAQ</Link></li>
          </ul>
          <div className={s.navActions}>
            <Link to="/login" className={s.navSignIn}>Sign in</Link>
            <Link to="/signup" className={s.navCta}>Start free trial</Link>
          </div>
        </div>
      </nav>

      <section className={s.hero}>
        <div className={s.heroBadge}>Resource Forecasting Platform</div>
        <h1 className={s.heroTitle}>
          Know exactly who's working on<br /><span>what, when, and for how much</span>
        </h1>
        <p className={s.heroSub}>
          AllocatIQ is the all-in-one platform for professional services teams to plan capacity, forecast revenue, and track utilization — without spreadsheets.
        </p>
        <div className={s.heroActions}>
          <Link to="/signup" className={s.heroCtaPrimary}>Start 14-day free trial →</Link>
          <a href="#features" className={s.heroCtaSecondary}>See how it works</a>
        </div>
        <p className={s.heroNote}>No credit card required · 14-day free trial · Cancel anytime</p>

        <div className={s.heroDemoCard}>
          <div className={s.demoBrowser}>
            <div className={s.demoDot} style={{ background: '#ff5f57' }} />
            <div className={s.demoDot} style={{ background: '#ffbd2e' }} />
            <div className={s.demoDot} style={{ background: '#28c840' }} />
            <div className={s.demoUrl}>app.allocatiq.ai / reports / gantt</div>
          </div>
          <div className={s.demoScreen}>
            <div className={s.demoSidebar}>
              {['Dashboard', 'Projects', 'Resources', 'Allocations', 'Reports', 'Settings'].map((item, i) => (
                <div key={item} className={`${s.demoNavItem} ${i === 4 ? s.demoActive : ''}`}>
                  <div className={s.demoNavDot} />{item}
                </div>
              ))}
            </div>
            <div className={s.demoContent}>
              <div style={{ marginBottom: 12, fontSize: 11, fontWeight: 700, color: '#323130' }}>RESOURCE ALLOCATION — Q1 2026</div>
              {[
                { name: 'Alice Chen', bars: [{ left: '0%', width: '45%', color: '#107c10' }, { left: '50%', width: '35%', color: '#0078d4' }] },
                { name: 'Bob Kumar', bars: [{ left: '5%', width: '60%', color: '#107c10' }] },
                { name: 'Carol Diaz', bars: [{ left: '0%', width: '30%', color: '#ff7b00' }, { left: '35%', width: '50%', color: '#107c10' }] },
                { name: 'David Lee', bars: [{ left: '20%', width: '70%', color: '#2D55A5' }] },
                { name: 'Eve Patel', bars: [{ left: '0%', width: '80%', color: '#107c10' }] },
              ].map(row => (
                <div key={row.name} className={s.demoGanttRow}>
                  <div className={s.demoGanttLabel}>{row.name}</div>
                  <div className={s.demoGanttTrack}>
                    {row.bars.map((bar, i) => (
                      <div key={i} className={s.demoGanttBar} style={{ left: bar.left, width: bar.width, background: bar.color }} />
                    ))}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 16, display: 'flex', gap: 12, fontSize: 10, flexWrap: 'wrap' }}>
                {[['#2D55A5', 'Overbooked'], ['#107c10', 'Full (8h)'], ['#ff7b00', 'Partial'], ['#8a8886', 'Non-billable']].map(([c, l]) => (
                  <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#605e5c' }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: c as string, display: 'inline-block' }} />{l}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={s.logos}>
        <div className={s.logosLabel}>Trusted by professional services teams</div>
        <div className={s.logosRow}>
          {['Consulting Firms', 'IT Services Teams', 'Managed Service Providers', 'Digital Agencies', 'Systems Integrators', 'PMO Offices'].map(name => (
            <div key={name} className={s.logoItem}>{name}</div>
          ))}
        </div>
      </section>

      <section className={s.features} id="features">
        <div className={s.sectionBadge}>Features</div>
        <h2 className={s.sectionTitle}>Everything you need to run a data-driven delivery team</h2>
        <p className={s.sectionSub}>Built for professional services firms who need more than basic project tracking.</p>
        <div className={s.featureGrid}>
          {features.map(f => (
            <div key={f.title} className={s.featureCard}>
              <div className={s.featureIcon} style={{ background: f.color }}>{f.icon}</div>
              <div className={s.featureCardTitle}>{f.title}</div>
              <div className={s.featureCardDesc}>{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className={s.pricing} id="pricing">
        <div className={s.pricingInner}>
          <div className={s.sectionBadge}>Pricing</div>
          <h2 className={s.sectionTitle}>Pay only for managers and admins</h2>
          <p className={s.sectionSub} style={{ margin: '0 auto' }}>Unlimited viewers and resource users on all plans. Only active manager and admin seats count. 14-day free trial included.</p>
          <div className={s.pricingGrid}>
            {plans.map(plan => (
              <div key={plan.name} className={`${s.pricingCard} ${plan.popular ? s.popular : ''}`}>
                {plan.popular && <span className={s.popularBadge}>Most Popular</span>}
                <div className={s.planName}>{plan.name}</div>
                <div className={s.planPrice}>
                  <span className={s.planCurrency}>$</span>
                  <span className={s.planAmount}>{plan.amount}</span>
                </div>
                <div className={s.planPeriod}>{plan.period}</div>
                <div className={s.planDesc}>{plan.desc}</div>
                <ul className={s.planFeatures}>
                  {plan.features.map(f => (
                    <li key={f}><span className={s.checkmark}>✓</span>{f}</li>
                  ))}
                </ul>
                <Link to="/signup" className={`${s.planBtn} ${s[plan.btnClass as keyof typeof s]}`}>
                  {plan.popular ? 'Start free trial →' : 'Get started'}
                </Link>
              </div>
            ))}
          </div>
          <div style={{
            maxWidth: '800px',
            margin: '32px auto 0',
            padding: '20px',
            background: '#f3f2f1',
            borderRadius: '8px',
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#323130'
          }}>
            <strong style={{ display: 'block', marginBottom: '8px' }}>How seat-based pricing works:</strong>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Paid Seats:</strong> Only Owner, Admin, and Manager roles count against your seat limit</li>
              <li><strong>Unlimited Viewers:</strong> Add as many read-only users as you need at no extra cost</li>
              <li><strong>Unlimited Resources:</strong> Contractors and consultants can log timesheets without using a paid seat</li>
              <li><strong>Flexible Growth:</strong> Add more manager/admin seats as needed at ${plans[1].features[3]?.split('$')[1]?.split('/')[0] || '15/month'} (Pro) or ${plans[2].features[3]?.split('$')[1]?.split('/')[0] || '12/month'} (Enterprise)</li>
            </ul>
          </div>
        </div>
      </section>

      <section className={s.testimonials}>
        <div className={s.sectionBadge}>Testimonials</div>
        <h2 className={s.sectionTitle}>Loved by delivery leaders</h2>
        <div className={s.testimonialsGrid}>
          {testimonials.map(t => (
            <div key={t.name} className={s.testimonialCard}>
              <p className={s.testimonialQuote}>{t.quote}</p>
              <div className={s.testimonialAuthor}>
                <div className={s.testimonialAvatar} style={{ background: t.color }}>
                  {t.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div className={s.testimonialName}>{t.name}</div>
                  <div className={s.testimonialRole}>{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className={s.ctaBanner}>
        <h2 className={s.ctaBannerTitle}>Ready to take control of your resources?</h2>
        <p className={s.ctaBannerSub}>Join hundreds of professional services teams using AllocatIQ to plan smarter.</p>
        <div className={s.ctaBannerActions}>
          <Link to="/signup" className={s.ctaBannerPrimary}>Start your free trial →</Link>
          <Link to="/login" className={s.ctaBannerSecondary}>Sign in to your account</Link>
        </div>
      </section>

      <footer className={s.footer}>
        <div className={s.footerInner}>
          <div className={s.footerTop}>
            <div>
              <div className={s.footerBrand}>
                <div className={s.footerLogo}>
                  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" aria-hidden="true">
                    <path d="M10 26L18 10L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M13 21H23" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className={s.footerBrandName}>AllocatIQ</span>
              </div>
              <p className={s.footerTagline}>Resource forecasting and utilization for professional services teams.</p>
            </div>
            <div>
              <div className={s.footerColTitle}>Product</div>
              <ul className={s.footerLinks}>
                <li><a href="#features">Features</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/signup">Free trial</Link></li>
              </ul>
            </div>
            <div>
              <div className={s.footerColTitle}>Company</div>
              <ul className={s.footerLinks}>
                <li><Link to="/about">About</Link></li>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </div>
            <div>
              <div className={s.footerColTitle}>Legal</div>
              <ul className={s.footerLinks}>
                <li><Link to="/privacy">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <hr className={s.footerDivider} />
          <div className={s.footerBottom}>
            <span>© 2026 AllocatIQ. All rights reserved.</span>
            <span>Made for professional services teams</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
